import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Navbar from './Navbar';
import api from '../utils/api';
import { trackPageView, trackEvent } from '../utils/analytics';

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const EmptyStateTableRow = ({ colSpan, message }) => (
  <tr>
    <td colSpan={colSpan} style={{ textAlign: 'center', padding: '64px 24px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg style={{ width: '32px', height: '32px', color: 'var(--text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        </div>
        <div>
          <h4 style={{ color: 'var(--text-primary)', margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>No Data Available</h4>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '14px' }}>{message}</p>
        </div>
      </div>
    </td>
  </tr>
);

const getAvatarStyles = (name) => {
  const styles = [
    { bg: 'linear-gradient(135deg, #4f46e5, #818cf8)', shadow: 'rgba(79, 70, 229, 0.25)' },
    { bg: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', shadow: 'rgba(14, 165, 233, 0.25)' },
    { bg: 'linear-gradient(135deg, #10b981, #34d399)', shadow: 'rgba(16, 185, 129, 0.25)' },
    { bg: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.25)' },
    { bg: 'linear-gradient(135deg, #f43f5e, #fb7185)', shadow: 'rgba(244, 63, 94, 0.25)' },
    { bg: 'linear-gradient(135deg, #f59e0b, #fbbf24)', shadow: 'rgba(245, 158, 11, 0.25)' },
    { bg: 'linear-gradient(135deg, #ec4899, #f472b6)', shadow: 'rgba(236, 72, 153, 0.25)' }
  ];
  let hash = 0;
  const str = name || 'U';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % styles.length;
  return styles[index];
};

const TenantStatusBadge = ({ deletedAt }) => {
  const isSuspended = deletedAt !== null && deletedAt !== undefined;
  if (isSuspended) {
    return (
      <span style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '100px', fontSize: '12px', fontWeight: '700', border: '1px solid rgba(239, 68, 68, 0.2)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444' }}></div>
        Suspended
      </span>
    );
  }
  return (
    <span style={{ padding: '6px 12px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '100px', fontSize: '12px', fontWeight: '700', border: '1px solid rgba(16, 185, 129, 0.2)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></div>
      Active
    </span>
  );
};

const SkeletonBox = ({ width, height, borderRadius = '8px', style = {} }) => (
  <div className="skeleton-pulse" style={{ width, height, borderRadius, background: 'var(--bg-surface-alt)', ...style }}></div>
);

const TableSkeleton = () => (
  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' }}>
    {/* Header row skeleton */}
    <div style={{ display: 'flex', gap: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
      <SkeletonBox width="20%" height="24px" />
      <SkeletonBox width="25%" height="24px" />
      <SkeletonBox width="15%" height="24px" />
      <SkeletonBox width="20%" height="24px" />
      <SkeletonBox width="20%" height="24px" />
    </div>
    {/* Data rows skeleton */}
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 0', borderBottom: '1px solid var(--border-color)' }}>
        <SkeletonBox width="44px" height="44px" borderRadius="12px" />
        <SkeletonBox width="15%" height="20px" />
        <SkeletonBox width="22%" height="20px" />
        <SkeletonBox width="12%" height="20px" />
        <SkeletonBox width="18%" height="20px" />
        <SkeletonBox width="18%" height="20px" />
      </div>
    ))}
  </div>
);

const AnalyticsSkeleton = () => (
  <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '32px', width: '100%' }}>
    {/* Top 4 Stats Cards */}
    <div className="responsive-grid-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{ background: 'var(--bg-surface)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SkeletonBox width="120px" height="16px" />
            <SkeletonBox width="40px" height="40px" borderRadius="12px" />
          </div>
          <SkeletonBox width="80px" height="36px" />
        </div>
      ))}
    </div>
    {/* Charts Area */}
    <div className="responsive-grid-charts" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '24px' }}>
      <div style={{ background: 'var(--bg-surface)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
        <SkeletonBox width="150px" height="24px" style={{ marginBottom: '24px' }} />
        <SkeletonBox width="100%" height="280px" borderRadius="12px" />
      </div>
      <div style={{ background: 'var(--bg-surface)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
        <SkeletonBox width="150px" height="24px" style={{ marginBottom: '24px' }} />
        <SkeletonBox width="100%" height="280px" borderRadius="12px" />
      </div>
    </div>
  </div>
);

const MiniSparkline = ({ seed = 1, isDeleted = false }) => {
  const generatePoints = () => {
    const points = [];
    const base = (Number(seed) % 7) * 4 + 12;
    for (let i = 0; i < 7; i++) {
      const val = Math.max(4, Math.min(36, base + Math.sin(Number(seed) + i * 1.4) * 12 + (i * 2)));
      points.push(val);
    }
    return points;
  };

  const data = generatePoints();
  const width = 84;
  const height = 24;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const strokeColor = isDeleted ? '#ef4444' : '#10b981';
  const gradientId = `sparkline-grad-${seed}`;

  const pointsString = data.map((val, idx) => {
    const x = (idx / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 8) - 4;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const pathD = `M ${pointsString.join(' L ')}`;
  const areaD = `${pathD} L ${width},${height} L 0,${height} Z`;

  const lastVal = data[data.length - 1];
  const lastX = width;
  const lastY = height - ((lastVal - min) / range) * (height - 8) - 4;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <svg width={width} height={height} style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#${gradientId})`} />
        <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={lastX} cy={lastY} r="2.5" fill={strokeColor} />
      </svg>
    </div>
  );
};

const ToastContainer = ({ toasts, removeToast }) => (
  <div style={{
    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: '12px', pointerEvents: 'none'
  }}>
    {toasts.map(toast => (
      <div key={toast.id} className="toast-enter" style={{
        background: toast.type === 'error' ? 'var(--bg-card)' : 'var(--bg-card)',
        color: 'var(--text-primary)', padding: '16px 20px', borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)', borderLeft: `4px solid ${toast.type === 'error' ? '#ef4444' : '#10b981'}`,
        display: 'flex', alignItems: 'center', gap: '12px', pointerEvents: 'auto',
        border: '1px solid var(--border-color)', width: '320px'
      }}>
        <div style={{ 
          background: toast.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', 
          color: toast.type === 'error' ? '#ef4444' : '#10b981', 
          width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' 
        }}>
          {toast.type === 'error' ? (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '14px', height: '14px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          ) : (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '14px', height: '14px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          )}
        </div>
        <div style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>{toast.message}</div>
        <button onClick={() => removeToast(toast.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '16px', height: '16px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    ))}
  </div>
);

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [superadmins, setSuperadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics', 'clients' or 'superadmins'
  const [searchQuery, setSearchQuery] = useState('');

  // Track page views when active tab changes
  useEffect(() => {
    trackPageView('/superadmin?tab=' + activeTab, 'Superadmin - ' + activeTab);
  }, [activeTab]);
  const [selectedTenants, setSelectedTenants] = useState([]);
  
  const searchInputRef = useRef(null);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  const toggleSelectAll = () => {
    if (selectedTenants.length === filteredTenants.length && filteredTenants.length > 0) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(filteredTenants.map(t => t.id));
    }
  };

  const toggleSelectTenant = (id) => {
    setSelectedTenants(prev => 
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };

  const exportSelectedToCSV = () => {
    const selectedData = tenants.filter(t => selectedTenants.includes(t.id));
    if (selectedData.length === 0) return;
    const headers = ['ID', 'Organization Name', 'Subdomain', 'Created By', 'Created Date', 'Status'];
    const csvContent = [
      headers.join(','),
      ...selectedData.map(t => [t.id, `"${t.org_name}"`, t.subdomain, t.created_by || 'Self Signup', t.created_at, t.deleted_at ? 'Deleted' : 'Active'].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `selected_tenants_${selectedTenants.length}.csv`;
    link.click();
    addToast(`Exported ${selectedTenants.length} tenants successfully!`);
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K for Search Focus / Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Cmd+N or Ctrl+N for New Client
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setShowModal('create');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const exportToCSV = () => {
    trackEvent('export_data', { tab: activeTab });
    if (!tenants || tenants.length === 0) return addToast('No data to export', 'error');
    
    const headers = ['ID', 'Organization Name', 'Subdomain', 'Created By', 'Created Date', 'Status'];
    const filteredTenants = tenants.filter(row => {
      if (!searchQuery.trim()) return true;
      return Object.values(row).some(val =>
        String(val || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    });

    const rows = filteredTenants.map(t => [
      t.id,
      `"${t.org_name}"`,
      t.subdomain,
      `"${t.created_by || 'Self Signup'}"`,
      new Date(t.created_at).toLocaleDateString(),
      t.deleted_at ? 'Suspended' : 'Active'
    ]);
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `client_tenants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast('Data exported successfully!');
  };

  // Toast state
  const [toasts, setToasts] = useState([]);
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 5000);
  };
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  
  // Modal states
  const [showModal, setShowModal] = useState(null); // 'create', 'edit', 'delete', 'create_superadmin', 'edit_superadmin', 'delete_superadmin', null
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedSuperadmin, setSelectedSuperadmin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Tenant Details States
  const [selectedTenantDetail, setSelectedTenantDetail] = useState(null);
  const [fetchingDetail, setFetchingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Change Password States
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [changePasswordSubmitting, setChangePasswordSubmitting] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    orgName: '',
    subdomain: '',
    adminName: '',
    adminEmail: '',
    adminPhone: ''
  });

  const [editForm, setEditForm] = useState({
    orgName: ''
  });

  const [superadminForm, setSuperadminForm] = useState({
    username: '',
    email: '',
    password: ''
  });

  const [masterActiveTable, setMasterActiveTable] = useState('superadmins');
  const [masterTableData, setMasterTableData] = useState({ columns: [], rows: [] });
  const [masterLoadingTable, setMasterLoadingTable] = useState(false);
  const [masterTableError, setMasterTableError] = useState('');
  const [masterSearchQuery, setMasterSearchQuery] = useState('');

  const [masterEditingRow, setMasterEditingRow] = useState(null);
  const [masterEditFormData, setMasterEditFormData] = useState({});
  const [masterEditSubmitting, setMasterEditSubmitting] = useState(false);
  const [masterEditError, setMasterEditError] = useState('');
  const [masterUnlockedFields, setMasterUnlockedFields] = useState({});

  const MASTER_TABLES = [
    { key: 'superadmins', label: 'Superadmins' },
    { key: 'tenants', label: 'Client Tenants' },
    { key: 'otps', label: 'One-Time Passwords (OTPs)' },
    { key: 'contact_submissions', label: 'Contact Submissions' }
  ];

  const fetchMasterTableData = async (tableName) => {
    setMasterLoadingTable(true);
    setMasterTableError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      const res = await api.get(`/api/superadmin/master/tables/${tableName}`, { headers });
      if (res.data && res.data.success) {
        setMasterTableData({
          columns: res.data.columns || [],
          rows: res.data.rows || []
        });
      } else {
        setMasterTableError(`Failed to load data for master table: ${tableName}`);
      }
    } catch (err) {
      setMasterTableError(err.response?.data?.error || `Failed to fetch data for ${tableName}`);
    } finally {
      setMasterLoadingTable(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'superadmins') {
      fetchMasterTableData(masterActiveTable);
    }
  }, [activeTab, masterActiveTable]);

  const getMasterPrimaryKeyColumn = (tableName) => {
    if (tableName === 'otps') return 'email';
    if (tableName === 'contact_submissions') return 'id';
    return 'id'; // Default for superadmins, tenants
  };

  const handleMasterEditClick = (row) => {
    setMasterEditingRow(row);
    setMasterEditFormData({ ...row });
    setMasterUnlockedFields({});
    setMasterEditError('');
  };

  const handleMasterEditSubmit = async (e) => {
    e.preventDefault();
    setMasterEditSubmitting(true);
    setMasterEditError('');

    const pkColumn = getMasterPrimaryKeyColumn(masterActiveTable);
    const pkValue = masterEditingRow[pkColumn];

    try {
      const headers = { 'x-user-id': currentUser.id };
      const payload = {
        pkColumn,
        pkValue,
        data: masterEditFormData
      };

      await api.put(`/api/superadmin/master/tables/${masterActiveTable}/rows`, payload, { headers });

      setMasterEditingRow(null);
      fetchMasterTableData(masterActiveTable);
      fetchData();
    } catch (err) {
      setMasterEditError(err.response?.data?.error || 'Failed to update row data.');
    } finally {
      setMasterEditSubmitting(false);
    }
  };

  const handleMasterInputChange = (colName, val) => {
    setMasterEditFormData(prev => ({
      ...prev,
      [colName]: val
    }));
  };

  // Check auth
  const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
  const isSuperadmin = currentUser.user_type === 'SUPERADMIN';

  useEffect(() => {
    if (!isSuperadmin) {
      navigate('/superadmin/login');
      return;
    }
    fetchData();
  }, [isSuperadmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'x-user-id': currentUser.id };
      const [tenantsRes, superadminsRes] = await Promise.all([
        api.get('/api/superadmin/tenants', { headers }),
        api.get('/api/superadmin/superadmins', { headers })
      ]);
      setTenants(tenantsRes.data);
      setSuperadmins(superadminsRes.data);
    } catch (err) {
      console.error('Failed to fetch superadmin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetails = async (tenant) => {
    setFetchingDetail(true);
    setDetailError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      const res = await api.get(`/api/superadmin/tenants/${tenant.id}/detail`, { headers });
      if (res.data && res.data.success) {
        setSelectedTenantDetail(res.data);
      } else {
        setDetailError('Failed to load client tenant details');
      }
    } catch (err) {
      setDetailError(err.response?.data?.error || 'Failed to connect to tenant database');
    } finally {
      setFetchingDetail(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_user');
    navigate('/superadmin/login');
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangePasswordSubmitting(true);
    setChangePasswordError('');
    setChangePasswordSuccess('');

    const { currentPassword, newPassword, confirmNewPassword } = changePasswordForm;

    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match');
      setChangePasswordSubmitting(false);
      return;
    }

    if (newPassword.length < 6) {
      setChangePasswordError('Password must be at least 6 characters long');
      setChangePasswordSubmitting(false);
      return;
    }

    try {
      const headers = { 'x-user-id': currentUser.id };
      const payload = { currentPassword, newPassword };
      const res = await api.post('/api/superadmin/change-password', payload, { headers });

      if (res.data && res.data.success) {
        setChangePasswordSuccess('Password reset successfully!');
        setChangePasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        });
        addToast('Password changed successfully!');
        setTimeout(() => {
          setShowChangePassword(false);
          setChangePasswordSuccess('');
          setShowCurrentPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
        }, 1500);
      } else {
        setChangePasswordError(res.data?.error || 'Failed to reset password');
      }
    } catch (err) {
      setChangePasswordError(err.response?.data?.error || 'Failed to communicate with server');
    } finally {
      setChangePasswordSubmitting(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.post('/api/superadmin/tenants', createForm, { headers });
      setShowModal(null);
      setCreateForm({ orgName: '', subdomain: '', adminName: '', adminEmail: '', adminPhone: '' });
      fetchData();
      addToast('Client created successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create tenant client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.put(`/api/superadmin/tenants/${selectedTenant.id}`, editForm, { headers });
      setShowModal(null);
      setSelectedTenant(null);
      fetchData();
      addToast('Client updated successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update tenant client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.delete(`/api/superadmin/tenants/${selectedTenant.id}`, { headers });
      setShowModal(null);
      setSelectedTenant(null);
      fetchData();
      addToast('Client deleted successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to delete tenant client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSuperadminSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.post('/api/superadmin/superadmins', superadminForm, { headers });
      setShowModal(null);
      setSuperadminForm({ username: '', email: '', password: '' });
      fetchData();
      addToast('Superadmin created successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create superadmin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSuperadminSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.put(`/api/superadmin/superadmins/${selectedSuperadmin.id}`, superadminForm, { headers });
      setShowModal(null);
      setSelectedSuperadmin(null);
      setSuperadminForm({ username: '', email: '', password: '' });
      fetchData();
      addToast('Superadmin updated successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update superadmin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSuperadminSubmit = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      const headers = { 'x-user-id': currentUser.id };
      await api.delete(`/api/superadmin/superadmins/${selectedSuperadmin.id}`, { headers });
      setShowModal(null);
      setSelectedSuperadmin(null);
      fetchData();
      addToast('Superadmin deleted successfully!');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to delete superadmin');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setEditForm({ orgName: tenant.org_name });
    setShowModal('edit');
  };

  const openDeleteModal = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal('delete');
  };

  const openCreateSuperadminModal = () => {
    setSuperadminForm({ username: '', email: '', password: '' });
    setShowModal('create_superadmin');
  };

  const openEditSuperadminModal = (sa) => {
    setSelectedSuperadmin(sa);
    setSuperadminForm({ username: sa.username, email: sa.email, password: '' });
    setShowModal('edit_superadmin');
  };

  const openDeleteSuperadminModal = (sa) => {
    setSelectedSuperadmin(sa);
    setShowModal('delete_superadmin');
  };

  const filteredTenants = tenants.filter(t => 
    t.org_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.subdomain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSuperadmins = superadmins.filter(sa => 
    sa.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sa.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isSuperadmin) return null;

  const CommandPalette = () => {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
      if (showCommandPalette) {
        setTimeout(() => inputRef.current?.focus(), 50);
        setQuery('');
      }
    }, [showCommandPalette]);

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') setShowCommandPalette(false);
      };
      if (showCommandPalette) window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    if (!showCommandPalette) return null;

    const actions = [
      { id: 'analytics', title: 'Go to Analytics Dashboard', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      { id: 'clients', title: 'Manage Clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
      { id: 'superadmins', title: 'Manage Superadmins', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
      { id: 'create', title: 'Provision New Client', icon: 'M12 4v16m8-8H4' }
    ];

    const filteredTenants = tenants.filter(t => t.org_name.toLowerCase().includes(query.toLowerCase()) || t.subdomain.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
    const filteredSuperadmins = (superadmins || []).filter(s => (s.username || '').toLowerCase().includes(query.toLowerCase()) || (s.email || '').toLowerCase().includes(query.toLowerCase())).slice(0, 5);
    const filteredActions = actions.filter(a => a.title.toLowerCase().includes(query.toLowerCase()));

    const handleAction = (type, data) => {
      setShowCommandPalette(false);
      if (type === 'action') {
        if (data === 'create') setShowModal('create');
        else setActiveTab(data);
      } else if (type === 'client') {
        setActiveTab('clients');
        fetchTenantDetails(data);
      } else if (type === 'superadmin') {
        setActiveTab('superadmins');
        setMasterSearchQuery(data.username);
      }
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => setShowCommandPalette(false)}>
        <div className="palette-enter" style={{ width: '100%', maxWidth: '640px', background: 'var(--bg-card)', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <svg fill="none" viewBox="0 0 24 24" stroke="var(--text-muted)" style={{width: '22px', height: '22px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input ref={inputRef} type="text" placeholder="What do you need?" value={query} onChange={e => setQuery(e.target.value)} style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '20px', outline: 'none' }} />
            <div style={{ fontSize: '12px', background: 'var(--bg-surface)', padding: '4px 8px', borderRadius: '4px', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>ESC</div>
          </div>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
            {query && filteredTenants.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', padding: '0 12px' }}>Clients</div>
                {filteredTenants.map(t => (
                  <div key={t.id} onClick={() => handleAction('client', t)} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', borderRadius: '12px' }} className="interactive-row">
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #4f46e5, #0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px' }}>
                      {t.org_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '15px' }}>{t.org_name}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{t.subdomain}.yourdomain.com</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {query && filteredSuperadmins.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', padding: '0 12px' }}>Superadmins</div>
                {filteredSuperadmins.map(s => (
                  <div key={s.id || s.username} onClick={() => handleAction('superadmin', s)} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', borderRadius: '12px' }} className="interactive-row">
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '15px' }}>
                      {(s.username || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '15px' }}>{s.username}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{s.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', padding: '0 12px' }}>Actions</div>
              {filteredActions.map(a => (
                <div key={a.id} onClick={() => handleAction('action', a.id)} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', borderRadius: '12px' }} className="interactive-row">
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="var(--primary-color)" style={{width: '20px', height: '20px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={a.icon} /></svg>
                  </div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '15px' }}>{a.title}</div>
                </div>
              ))}
              {filteredActions.length === 0 && !query && (
                <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center' }}>No commands found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <CommandPalette />
      <Navbar />

      <div style={styles.pageLayout} className="superadmin-page-layout">
        {/* SIDEBAR */}
        {!selectedTenantDetail && !fetchingDetail && (
          <div style={styles.sidebar} className="superadmin-sidebar">
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ padding: '0 14px 6px 14px', fontSize: '10.5px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Main Console
              </div>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 8px' }}>
                <button
                  style={{ ...styles.sidebarItem, ...(activeTab === 'analytics' ? styles.sidebarItemActive : {}) }}
                  onClick={() => { setActiveTab('analytics'); setSelectedTenantDetail(null); setDetailError(''); }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '18px', height: '18px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Analytics</span>
                </button>
                <button
                  style={{ ...styles.sidebarItem, ...(activeTab === 'clients' ? styles.sidebarItemActive : {}) }}
                  onClick={() => { setActiveTab('clients'); setSelectedTenantDetail(null); setDetailError(''); }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '18px', height: '18px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>Clients</span>
                </button>
                <button
                  style={{ ...styles.sidebarItem, ...(activeTab === 'superadmins' ? styles.sidebarItemActive : {}) }}
                  onClick={() => { setActiveTab('superadmins'); setSelectedTenantDetail(null); setDetailError(''); }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '18px', height: '18px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Superadmins</span>
                </button>
              </nav>
            </div>

            <div className="sidebar-footer" style={{ padding: '10px 8px', borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg, #4f46e5, #0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '12px', flexShrink: 0 }}>
                  {(currentUser.username || 'A').charAt(0).toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden', flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentUser.username}
                  </div>
                  <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#10b981' }} /> Superadmin
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setShowChangePassword(true)} 
                style={{ ...styles.sidebarItem, fontSize: '12px', padding: '7px 10px', gap: '8px', width: '100%', borderRadius: '8px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '16px', height: '16px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Change Password
              </button>
              <button 
                onClick={handleLogout} 
                style={{ ...styles.sidebarItem, fontSize: '12px', padding: '7px 10px', gap: '8px', width: '100%', color: '#ef4444', borderRadius: '8px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '16px', height: '16px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div style={{ ...styles.contentArea, ...(selectedTenantDetail || fetchingDetail ? { padding: '24px' } : {}) }} className="superadmin-content">
          {detailError && (
            <div style={{...styles.modalError, marginBottom: '20px'}}>
              🚨 {detailError}
            </div>
          )}

          {fetchingDetail ? (
            <TableSkeleton />
          ) : selectedTenantDetail ? (
            <TenantDetailView 
              data={selectedTenantDetail} 
              addToast={addToast}
              onBack={() => { setSelectedTenantDetail(null); setDetailError(''); }} 
            />
          ) : (
            <div key={activeTab} className="slide-fade-in" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>{activeTab === 'clients' ? 'Client Tenants' : activeTab === 'analytics' ? 'Analytics Dashboard' : 'Superadmin Management'}</h1>
              <p style={styles.subtitle}>{activeTab === 'clients' ? 'Manage clients and provision environments' : activeTab === 'analytics' ? 'Actionable insights and real-time platform metrics' : 'Track and manage admin users'}</p>
            </div>

            <div className="responsive-header" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: 'var(--text-muted)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder={activeTab === 'superadmins' ? "Search superadmins & master tables... (Cmd+K)" : "Search clients... (Cmd+K)"}
                  value={activeTab === 'superadmins' ? masterSearchQuery : searchQuery}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (activeTab === 'superadmins') {
                      setMasterSearchQuery(val);
                    } else {
                      setSearchQuery(val);
                      if (activeTab === 'analytics') {
                        setActiveTab('clients');
                      }
                    }
                  }}
                  style={{ ...styles.searchInput, paddingLeft: '42px', width: '300px', borderRadius: '100px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)' }}
                />
              </div>
              <button onClick={exportToCSV} style={{ ...styles.createButton, background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '100px' }} title="Export as CSV">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '18px', height: '18px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Data
              </button>
              <button onClick={() => setShowModal('create')} style={{ ...styles.createButton, display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '100px' }} title="Provision Client (Cmd+N)">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '18px', height: '18px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                Provision Client
              </button>
            </div>
              </div>

          {/* Stats Cards removed as they are now in the Analytics tab */}

          {/* Main Workspace */}
          <div style={styles.workspace}>
            {loading ? (
              <TableSkeleton />
            ) : activeTab === 'clients' ? (
              /* Clients Table */
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedTenants.length === filteredTenants.length && filteredTenants.length > 0} 
                          onChange={toggleSelectAll}
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: 'var(--primary-color)' }}
                        />
                      </th>
                      <th style={styles.th}>Organization Name</th>
                      <th style={styles.th}>Subdomain</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Activity Trend (7d)</th>
                      <th style={styles.th}>Total Interviews</th>
                      <th style={styles.th}>Created By</th>
                      <th style={styles.th}>Created Date</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.length === 0 ? (
                      <EmptyStateTableRow colSpan="9" message="No client tenants found matching search." />
                    ) : filteredTenants.map((t, idx) => (
                      <tr key={t.id} style={{ ...styles.tr, ...(selectedTenants.includes(t.id) ? { background: 'var(--bg-hover)' } : {}) }} className="interactive-row">
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedTenants.includes(t.id)} 
                            onChange={() => toggleSelectTenant(t.id)}
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: 'var(--primary-color)' }}
                          />
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => fetchTenantDetails(t)}
                            style={styles.tenantLinkBtn}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                            title="Click to view tenant directory and interview logs"
                          >
                            {t.org_name}
                          </button>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.subdomainBadge}>{t.subdomain}</span>
                        </td>
                        <td style={styles.td}>
                          <TenantStatusBadge deletedAt={t.deleted_at} />
                        </td>
                        <td style={styles.td}>
                          <MiniSparkline seed={t.id || idx} isDeleted={!!t.deleted_at} />
                        </td>
                        <td style={{...styles.td, fontWeight: '600', color: 'var(--text-primary)'}}>{t.total_interviews !== undefined ? t.total_interviews : 0}</td>
                        <td style={styles.td}>{t.created_by || 'Self Signup'}</td>
                        <td style={styles.td}>{new Date(t.created_at).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          <div className="row-actions">
                            <button onClick={() => openEditModal(t)} style={styles.editLink}>Edit</button>
                            <button onClick={() => openDeleteModal(t)} style={styles.deleteLink}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : activeTab === 'analytics' ? (
              <SuperadminAnalyticsView tenants={tenants} onViewClient={(tenant) => { setActiveTab('clients'); fetchTenantDetails(tenant); }} />
            ) : (
              /* Master Database Tables Inspector (Superadmins Tab) */
              <div style={styles.inspectorSplit}>
                {/* Left Sidebar Table Selector */}
                <div style={styles.inspectorSidebar}>
                  <h4 style={styles.inspectorSidebarTitle}>Master Tables</h4>
                  <div style={styles.inspectorList}>
                    {MASTER_TABLES.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => { setMasterActiveTable(t.key); setMasterSearchQuery(''); }}
                        style={{
                          ...styles.inspectorBtn,
                          ...(masterActiveTable === t.key ? styles.inspectorBtnActive : {})
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {/* Create Superadmin button in selector sidebar */}
                  {masterActiveTable === 'superadmins' && (
                    <button
                      onClick={openCreateSuperadminModal}
                      style={{
                        ...styles.createButton,
                        width: '100%',
                        marginTop: '20px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '13px'
                      }}
                    >
                      + Create Superadmin
                    </button>
                  )}
                </div>

                {/* Right Data Grid Panel */}
                <div style={styles.inspectorMainPanel}>
                  <div style={styles.detailToolbar}>
                    <h3 style={styles.tableTitleText}>
                      Viewing Master Table: <code>{masterActiveTable}</code> ({masterTableData.rows.length} rows)
                    </h3>
                    <div style={styles.actions}>
                      <input
                        type="text"
                        placeholder="Filter rows..."
                        value={masterSearchQuery}
                        onChange={(e) => setMasterSearchQuery(e.target.value)}
                        style={styles.searchInput}
                      />
                    </div>
                  </div>

                  {masterTableError && (
                    <div style={styles.tableErrorBox}>
                      ⚠️ {masterTableError}
                    </div>
                  )}

                  {masterLoadingTable ? (
                    <TableSkeleton />
                  ) : (
                    <div style={styles.tableWrapper}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            {masterTableData.columns.map(col => (
                              <th key={col} style={styles.th}>{col}</th>
                            ))}
                            <th style={styles.th}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {masterTableData.rows.filter(row => {
                            if (!masterSearchQuery.trim()) return true;
                            return Object.values(row).some(val =>
                              String(val || '').toLowerCase().includes(masterSearchQuery.toLowerCase())
                            );
                          }).length === 0 ? (
                            <EmptyStateTableRow colSpan={masterTableData.columns.length + 1} message={`No records found inside ${masterActiveTable}.`} />
                          ) : (
                            masterTableData.rows.filter(row => {
                              if (!masterSearchQuery.trim()) return true;
                              return Object.values(row).some(val =>
                                String(val || '').toLowerCase().includes(masterSearchQuery.toLowerCase())
                              );
                            }).map((row, idx) => {
                              const pkCol = getMasterPrimaryKeyColumn(masterActiveTable);
                              const rowKey = row[pkCol] !== undefined ? `${row[pkCol]}-${idx}` : idx;
                              return (
                                <tr key={rowKey} style={styles.tr} className="interactive-row">
                                  {masterTableData.columns.map(col => {
                                    let valStr = row[col] === null || row[col] === undefined ? 'NULL' : String(row[col]);
                                    if (col === 'password_hash') valStr = '••••••••';
                                    return (
                                      <td key={col} style={styles.td} title={valStr}>
                                        <div style={{
                                          maxWidth: '240px',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          fontSize: '13px'
                                        }}>
                                          {valStr}
                                        </div>
                                      </td>
                                    );
                                  })}
                                  <td style={styles.td}>
                                    <button
                                      onClick={() => handleMasterEditClick(row)}
                                      style={styles.editRowLink}
                                    >
                                      Edit Row
                                    </button>
                                    {masterActiveTable === 'superadmins' && row[pkCol] !== currentUser.id && (
                                      <button
                                        onClick={() => openDeleteSuperadminModal(row)}
                                        style={{...styles.editRowLink, color: '#ef4444', marginLeft: '12px'}}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>

      {/* FLOATING BULK ACTION BAR */}
      {selectedTenants.length > 0 && (
        <div className="bulk-bar-enter" style={{
          position: 'fixed',
          bottom: '36px',
          left: '50%',
          zIndex: 9500,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          borderRadius: '100px',
          padding: '10px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backdropFilter: 'blur(12px)'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '700', background: 'var(--primary-color)', color: '#fff', padding: '4px 12px', borderRadius: '100px' }}>
            {selectedTenants.length} Selected
          </span>
          <button onClick={exportSelectedToCSV} style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{width: '16px', height: '16px'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Export Selected
          </button>
          <div style={{ width: '1px', height: '18px', background: 'var(--border-color)' }} />
          <button onClick={() => setSelectedTenants([])} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
            Deselect All
          </button>
        </div>
      )}



      {/* CREATE MODAL */}
      {showModal === 'create' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Provision New Client Workspace</h3>
            <form onSubmit={handleCreateSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Organization Name</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. Acme Corp"
                  value={createForm.orgName}
                  onChange={(e) => setCreateForm({...createForm, orgName: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unique Subdomain</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. acme"
                  value={createForm.subdomain}
                  onChange={(e) => setCreateForm({...createForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                  style={styles.formInput}
                />
                <span style={styles.inputHelp}>Lowercase letters and numbers only. Workspace will compile at: subdomain.virtualselect.in</span>
              </div>
              <div style={styles.formDivider}>Initial HR Administrator Account</div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Full Name</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. John Doe"
                  value={createForm.adminName}
                  onChange={(e) => setCreateForm({...createForm, adminName: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Email Address</label>
                <input 
                  type="email" 
                  required
                  placeholder="john.doe@company.com"
                  value={createForm.adminEmail}
                  onChange={(e) => setCreateForm({...createForm, adminEmail: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Phone Number</label>
                <input 
                  type="text" 
                  required
                  placeholder="+1 (555) 123-4567"
                  value={createForm.adminPhone}
                  onChange={(e) => setCreateForm({...createForm, adminPhone: e.target.value})}
                  style={styles.formInput}
                />
              </div>

              {formError && <p style={styles.modalError}>{formError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(null)} style={styles.cancelButton} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={submitting}>
                  {submitting ? 'Provisioning...' : 'Provision Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showModal === 'edit' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Modify Client Identity</h3>
            <form onSubmit={handleEditSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Organization Name</label>
                <input 
                  type="text" 
                  required
                  value={editForm.orgName}
                  onChange={(e) => setEditForm({...editForm, orgName: e.target.value})}
                  style={styles.formInput}
                />
              </div>

              {formError && <p style={styles.modalError}>{formError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => { setShowModal(null); setSelectedTenant(null); }} style={styles.cancelButton} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showModal === 'delete' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{...styles.modalTitle, color: '#f87171'}}>Complete Teardown & Deletion</h3>
            <p style={styles.modalWarnText}>
              Are you sure you want to completely delete client workspace <strong>{selectedTenant?.org_name}</strong>?
            </p>
            <p style={{...styles.modalWarnText, color: '#f87171', fontSize: '13px', fontWeight: 'bold'}}>
              ⚠️ CRITICAL WARNING: This action will permanently drop the tenant database and delete all associated global users, candidates, and records. This action is absolute and cannot be undone.
            </p>

            {formError && <p style={styles.modalError}>{formError}</p>}

            <div style={styles.modalActions}>
              <button type="button" onClick={() => { setShowModal(null); setSelectedTenant(null); }} style={styles.cancelButton} disabled={submitting}>
                Cancel
              </button>
              <button onClick={handleDeleteSubmit} style={styles.dangerButton} disabled={submitting}>
                {submitting ? 'Tearing down...' : 'Complete Destruction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE SUPERADMIN MODAL */}
      {showModal === 'create_superadmin' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Create New Superadmin</h3>
            <form onSubmit={handleCreateSuperadminSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. admin_alex"
                  value={superadminForm.username}
                  onChange={(e) => setSuperadminForm({...superadminForm, username: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <input 
                  type="email" 
                  required
                  placeholder="alex@company.com"
                  value={superadminForm.email}
                  onChange={(e) => setSuperadminForm({...superadminForm, email: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input 
                  type="password" 
                  required
                  placeholder="••••••••"
                  value={superadminForm.password}
                  onChange={(e) => setSuperadminForm({...superadminForm, password: e.target.value})}
                  style={styles.formInput}
                />
              </div>

              {formError && <p style={styles.modalError}>{formError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(null)} style={styles.cancelButton} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Superadmin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT SUPERADMIN MODAL */}
      {showModal === 'edit_superadmin' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Modify Superadmin Identity</h3>
            <form onSubmit={handleEditSuperadminSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username</label>
                <input 
                  type="text" 
                  required
                  value={superadminForm.username}
                  onChange={(e) => setSuperadminForm({...superadminForm, username: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <input 
                  type="email" 
                  required
                  value={superadminForm.email}
                  onChange={(e) => setSuperadminForm({...superadminForm, email: e.target.value})}
                  style={styles.formInput}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>New Password (Optional)</label>
                <input 
                  type="password" 
                  placeholder="Leave blank to keep current"
                  value={superadminForm.password}
                  onChange={(e) => setSuperadminForm({...superadminForm, password: e.target.value})}
                  style={styles.formInput}
                />
              </div>

              {formError && <p style={styles.modalError}>{formError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => { setShowModal(null); setSelectedSuperadmin(null); }} style={styles.cancelButton} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE SUPERADMIN MODAL */}
      {showModal === 'delete_superadmin' && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{...styles.modalTitle, color: '#f87171'}}>Delete Superadmin</h3>
            <p style={styles.modalWarnText}>
              Are you sure you want to completely delete superadmin user <strong>{selectedSuperadmin?.username}</strong> ({selectedSuperadmin?.email})?
            </p>
            <p style={{...styles.modalWarnText, color: '#f87171', fontSize: '13px', fontWeight: 'bold'}}>
              ⚠️ Warning: This will permanently revoke their master console access. This action cannot be undone.
            </p>

            {formError && <p style={styles.modalError}>{formError}</p>}

            <div style={styles.modalActions}>
              <button type="button" onClick={() => { setShowModal(null); setSelectedSuperadmin(null); }} style={styles.cancelButton} disabled={submitting}>
                Cancel
              </button>
              <button onClick={handleDeleteSuperadminSubmit} style={styles.dangerButton} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Confirm Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DYNAMIC MASTER EDIT MODAL */}
      {masterEditingRow && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto'}}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, margin: 0}}>Modify Master Table Record</h3>
              <button 
                type="button" 
                onClick={() => setMasterEditingRow(null)} 
                style={styles.modalCloseX}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            <p style={{fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px', marginTop: '-8px'}}>
              Table: <code>{masterActiveTable}</code> | Primary Key: <code>{getMasterPrimaryKeyColumn(masterActiveTable)} = {masterEditingRow[getMasterPrimaryKeyColumn(masterActiveTable)]}</code>
            </p>

            <form onSubmit={handleMasterEditSubmit} style={styles.form}>
              {masterTableData.columns.map((col) => {
                const isPk = col === getMasterPrimaryKeyColumn(masterActiveTable);
                const isReadonlyMeta = col === 'created_at' || col === 'updated_at' || col === 'password_hash';
                const isFieldDisabled = isPk || isReadonlyMeta;
                const isEditable = !isFieldDisabled;

                return (
                  <div key={col} style={styles.formGroup}>
                    <label style={{...styles.label, fontWeight: '700'}}>
                      {col} {isFieldDisabled && <span style={{fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal'}}>(Read-Only)</span>}
                    </label>

                    <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                      {masterUnlockedFields[col] || isFieldDisabled ? (
                        // Edit/Readonly Input
                        typeof masterEditingRow[col] === 'string' && masterEditingRow[col].length > 100 ? (
                          <textarea
                            disabled={isFieldDisabled}
                            value={masterEditFormData[col] === null || masterEditFormData[col] === undefined ? '' : masterEditFormData[col]}
                            onChange={(e) => handleMasterInputChange(col, e.target.value)}
                            style={{...styles.formInput, width: '100%', paddingRight: '36px', minHeight: '60px', fontFamily: 'inherit', resize: 'vertical'}}
                          />
                        ) : (
                          <input
                            type="text"
                            disabled={isFieldDisabled}
                            value={masterEditFormData[col] === null || masterEditFormData[col] === undefined ? '' : masterEditFormData[col]}
                            onChange={(e) => handleMasterInputChange(col, e.target.value)}
                            style={{...styles.formInput, width: '100%', paddingRight: '36px'}}
                          />
                        )
                      ) : (
                        // Locked Display Input (looks read-only)
                        <input
                          type="text"
                          disabled
                          value={masterEditFormData[col] === null || masterEditFormData[col] === undefined ? 'NULL' : masterEditFormData[col]}
                          style={{
                            ...styles.formInput,
                            width: '100%',
                            paddingRight: '36px',
                            background: 'var(--bg-surface-alt)',
                            borderColor: 'transparent',
                            cursor: 'default',
                            color: 'var(--text-primary)',
                            opacity: 0.95,
                          }}
                        />
                      )}

                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => {
                            setMasterUnlockedFields(prev => ({
                              ...prev,
                              [col]: !prev[col]
                            }));
                          }}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: typeof masterEditingRow[col] === 'string' && masterEditingRow[col].length > 100 ? '10px' : '50%',
                            transform: typeof masterEditingRow[col] === 'string' && masterEditingRow[col].length > 100 ? 'none' : 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px',
                            borderRadius: '4px',
                            color: masterUnlockedFields[col] ? '#10b981' : 'var(--text-muted)',
                            zIndex: 2,
                            transition: 'color 0.15s ease'
                          }}
                          title={masterUnlockedFields[col] ? "Lock & save field value" : "Unlock field to edit"}
                        >
                          {masterUnlockedFields[col] ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4z" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {masterEditError && <p style={styles.modalError}>{masterEditError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setMasterEditingRow(null)} style={styles.cancelButton} disabled={masterEditSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={masterEditSubmitting}>
                  {masterEditSubmitting ? 'Saving changes...' : 'Save Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {showChangePassword && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: '400px'}}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, margin: 0}}>Reset Password</h3>
              <button 
                type="button" 
                onClick={() => {
                  setShowChangePassword(false);
                  setChangePasswordError('');
                  setChangePasswordSuccess('');
                  setShowCurrentPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }} 
                style={styles.modalCloseX}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleChangePasswordSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Current Password</label>
                <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                  <input 
                    type={showCurrentPassword ? 'text' : 'password'} 
                    required
                    value={changePasswordForm.currentPassword}
                    onChange={(e) => setChangePasswordForm({...changePasswordForm, currentPassword: e.target.value})}
                    style={{...styles.formInput, width: '100%', paddingRight: '36px'}}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      color: 'var(--text-muted)',
                      transition: 'color 0.15s ease',
                      zIndex: 2,
                    }}
                  >
                    {showCurrentPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                  <input 
                    type={showNewPassword ? 'text' : 'password'} 
                    required
                    value={changePasswordForm.newPassword}
                    onChange={(e) => setChangePasswordForm({...changePasswordForm, newPassword: e.target.value})}
                    style={{...styles.formInput, width: '100%', paddingRight: '36px'}}
                    placeholder="Enter new password (min. 6 chars)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      color: 'var(--text-muted)',
                      transition: 'color 0.15s ease',
                      zIndex: 2,
                    }}
                  >
                    {showNewPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm New Password</label>
                <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                  <input 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    required
                    value={changePasswordForm.confirmNewPassword}
                    onChange={(e) => setChangePasswordForm({...changePasswordForm, confirmNewPassword: e.target.value})}
                    style={{...styles.formInput, width: '100%', paddingRight: '36px'}}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      color: 'var(--text-muted)',
                      transition: 'color 0.15s ease',
                      zIndex: 2,
                    }}
                  >
                    {showConfirmPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {changePasswordError && <p style={styles.modalError}>{changePasswordError}</p>}
              {changePasswordSuccess && <p style={{color: '#10b981', fontSize: '13px', margin: '0 0 16px 0', fontWeight: 'bold'}}>✓ {changePasswordSuccess}</p>}

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowChangePassword(false);
                    setChangePasswordError('');
                    setChangePasswordSuccess('');
                    setShowCurrentPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }} 
                  style={styles.cancelButton} 
                  disabled={changePasswordSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={changePasswordSubmitting}>
                  {changePasswordSubmitting ? 'Updating...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function SuperadminAnalyticsView({ tenants, onViewClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30'); // '7', '30', 'all'

  const fetchAnalytics = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
      const res = await api.get('/api/superadmin/analytics', {
        headers: { 'x-user-id': currentUser.id }
      });
      setData(res.data.data);
    } catch (err) {
      setError('Failed to load analytics: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);



  const filterByDateRange = (items, dateField) => {
    if (!items) return [];
    if (dateRange === 'all') return items;
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate() - parseInt(dateRange, 10));
    return items.filter(item => new Date(item[dateField]) >= past);
  };

  const chartData = filterByDateRange(data?.timelineData, 'date');
  const filteredLatestTenants = filterByDateRange(tenants, 'created_at');
  const latestTenants = [...(filteredLatestTenants || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const currentIndex = chartData.findIndex(d => d.date === label);
      const prevData = currentIndex > 0 ? chartData[currentIndex - 1].interviews : null;
      const currentVal = payload[0].value;
      
      let growthElement = null;
      if (prevData !== null) {
        if (prevData === 0) {
          growthElement = <div style={{ color: '#10b981', fontSize: '13px', fontWeight: '700', marginTop: '4px' }}>+100% vs yesterday</div>;
        } else {
          const growth = Math.round(((currentVal - prevData) / prevData) * 100);
          growthElement = (
            <div style={{ color: growth >= 0 ? '#10b981' : '#ef4444', fontSize: '13px', fontWeight: '700', marginTop: '4px' }}>
              {growth >= 0 ? '+' : ''}{growth}% vs yesterday
            </div>
          );
        }
      }

      return (
        <div style={{
          background: 'var(--bg-card)',
          backdropFilter: 'blur(8px)',
          border: '1px solid var(--border-color)',
          padding: '12px 16px',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-md)'
        }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: 'var(--text-muted)' }}>{label}</p>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--primary-color)' }}>
            {currentVal} Interviews
          </p>
          {growthElement}
        </div>
      );
    }
    return null;
  };

  if (loading) return <AnalyticsSkeleton />;
  if (error) return <div style={{ padding: '24px' }}><div style={styles.tableErrorBox}>{error}</div></div>;

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
        <select 
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          style={{ padding: '8px 16px', borderRadius: '100px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
        >
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>
      
      <div className="responsive-grid-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>

        {[
          { label: 'Total Tenants', value: data.totalTenants, color: '#4f46e5', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
          { label: 'Registered Users', value: data.totalUsers, color: '#0ea5e9', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
          { label: 'Total Interviews', value: data.totalInterviews, color: '#10b981', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
          { label: 'Total Candidates', value: data.totalCandidates, color: '#8b5cf6', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid var(--border-color)',
            borderTop: `3px solid ${stat.color}`,
            borderRadius: '16px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
            transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s ease',
            cursor: 'default'
          }}
          onMouseEnter={(e) => { 
            e.currentTarget.style.transform = 'translateY(-6px)'; 
            e.currentTarget.style.boxShadow = `0 15px 35px -5px ${stat.color}30`; 
            e.currentTarget.querySelector('.icon-wrapper').style.transform = 'scale(1.1) rotate(5deg)'; 
          }}
          onMouseLeave={(e) => { 
            e.currentTarget.style.transform = 'none'; 
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.03)'; 
            e.currentTarget.querySelector('.icon-wrapper').style.transform = 'none'; 
          }}
          >
            <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: `${stat.color}15`, borderRadius: '50%', filter: 'blur(20px)', zIndex: 0 }}></div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ color: stat.color, marginBottom: '20px', display: 'flex', alignItems: 'center', transition: 'transform 0.3s ease' }} className="icon-wrapper">
                <div style={{ background: `${stat.color}15`, padding: '12px', borderRadius: '12px', display: 'flex' }}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} style={{ width: '24px', height: '24px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={stat.icon} />
                  </svg>
                </div>
              </div>
              <h6 style={{ color: 'var(--text-muted)', fontSize: '14px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{stat.label}</h6>
              <h2 style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: '800', margin: 0 }}>{stat.value.toLocaleString()}</h2>
            </div>
          </div>
        ))}
      </div>

      <div className="responsive-grid-master" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <h5 style={{ fontSize: '18px', marginBottom: '32px', fontWeight: '700', color: 'var(--text-primary)' }}>Platform Activity</h5>
          <div style={{ width: '100%', height: '320px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorInterviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#4f46e5" strokeOpacity={0.15} />
                <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="interviews" stroke="#4f46e5" strokeWidth={4} fillOpacity={1} fill="url(#colorInterviews)" activeDot={{ r: 6, strokeWidth: 0, fill: '#4f46e5' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <h5 style={{ fontSize: '18px', marginBottom: '32px', fontWeight: '700', color: 'var(--text-primary)' }}>Tenant Distribution</h5>
          <div style={{ width: '100%', height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.slice(-14)} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#4f46e5" strokeOpacity={0.15} />
                 <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                 <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                 <Tooltip 
                  contentStyle={{ backgroundColor: 'var(--bg-card-glass)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid var(--border-color)', borderRadius: '12px', color: 'var(--text-primary)', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)' }}
                  itemStyle={{ color: '#0ea5e9', fontWeight: 800 }}
                  labelStyle={{ color: 'var(--text-secondary)', marginBottom: '4px' }}
                  cursor={{fill: 'var(--border-color)', opacity: 0.4}}
                 />
                 <Bar dataKey="interviews" fill="#0ea5e9" radius={[6, 6, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', padding: '28px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
          <h5 style={{ fontSize: '18px', margin: 0, fontWeight: '700', color: 'var(--text-primary)' }}>Latest Clients</h5>
        </div>
        
        <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>Client</th>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>Subdomain</th>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>Status</th>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>Created By</th>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>Joined Date</th>
                <th style={{ padding: '0 16px 16px 16px', fontWeight: '600', width: '40px', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}></th>
              </tr>
            </thead>
            <tbody>
              {latestTenants.length === 0 ? (
                <EmptyStateTableRow colSpan="6" message="No clients found in the system yet." />
              ) : latestTenants.map((tenant, idx) => (
                <tr key={tenant.id} className="interactive-row" style={{ borderBottom: idx === latestTenants.length - 1 ? 'none' : '1px solid var(--border-color)', transition: 'background-color 0.2s ease' }}>
                  <td style={{ padding: '20px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '12px', 
                        background: getAvatarStyles(tenant.org_name).bg, color: '#fff', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        fontWeight: '700', fontSize: '18px',
                        boxShadow: `0 4px 12px ${getAvatarStyles(tenant.org_name).shadow}`
                      }}>
                        {(tenant.org_name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--text-primary)', fontSize: '15px', marginBottom: '2px' }}>{tenant.org_name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>ID: {tenant.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '20px 16px' }}>
                    <span style={{ 
                      padding: '6px 14px', background: 'var(--bg-surface)', 
                      color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '100px', fontSize: '13px', fontWeight: '600' 
                    }}>
                      {tenant.subdomain}
                    </span>
                  </td>
                  <td style={{ padding: '20px 16px' }}>
                    <TenantStatusBadge deletedAt={tenant.deleted_at} />
                  </td>
                  <td style={{ padding: '20px 16px', color: 'var(--text-primary)', fontSize: '15px' }}>{tenant.created_by || 'Self Signup'}</td>
                  <td style={{ padding: '20px 16px', color: 'var(--text-muted)', fontSize: '14px' }}>{new Date(tenant.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                  <td style={{ padding: '20px 16px', textAlign: 'right' }}>
                    <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                      <button 
                        onClick={() => onViewClient && onViewClient(tenant)} 
                        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-input)', color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        View Data
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '14px', height: '14px' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const getStatusStyle = (status) => {
  switch (status) {
    case 'COMPLETED':
      return { backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' };
    case 'CANCELLED':
      return { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
    default:
      return { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' };
  }
};

const TenantDetailView = ({ data, onBack, addToast }) => {
  const { tenant } = data;
  const [activeTable, setActiveTable] = useState('users');
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const [loadingTable, setLoadingTable] = useState(false);
  const [tableError, setTableError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Edit row states
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [unlockedFields, setUnlockedFields] = useState({});

  // Delete states
  const [deleteRowItem, setDeleteRowItem] = useState(null);
  const [softDeleteRowItem, setSoftDeleteRowItem] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const TABLES = [
    { key: 'users', label: 'Team Directory' },
    { key: 'interview_rooms', label: 'Interviews' },
    { key: 'otps', label: 'One-Time Passwords (OTPs)' },
    { key: 'room_interviewers', label: 'Room Interviewers' },
    { key: 'candidate_profiles', label: 'Candidate Profiles' },
    { key: 'candidate_answers', label: 'Candidate Answers' },
    { key: 'interviewer_questions', label: 'Interviewer Questions' },
    { key: 'interview_transcripts', label: 'Transcripts' },
    { key: 'interview_screenshots', label: 'Screenshots' },
    { key: 'interview_report_email_logs', label: 'Email Report Logs' },
    { key: 'projects', label: 'Projects List' },
    { key: 'employees', label: 'Employees Roster' },
    { key: 'job_openings', label: 'Job Openings' },
    { key: 'screened_candidates', label: 'Screened Candidates' },
    { key: 'scheduled_interviews', label: 'Scheduled Interviews' },
    { key: 'interviewer_evaluations', label: 'Interviewer Evaluations' },
    { key: 'interviewer_rewards_log', label: 'Interviewer Rewards Log' }
  ];

  const fetchTableData = async (tableName) => {
    setLoadingTable(true);
    setTableError('');
    try {
      const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
      const headers = { 'x-user-id': currentUser.id };
      const res = await api.get(`/api/superadmin/tenants/${tenant.id}/tables/${tableName}`, { headers });
      if (res.data && res.data.success) {
        setTableData({
          columns: res.data.columns || [],
          rows: res.data.rows || []
        });
      } else {
        setTableError(`Failed to load data for table: ${tableName}`);
      }
    } catch (err) {
      setTableError(err.response?.data?.error || `Failed to fetch data for ${tableName}`);
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchTableData(activeTable);
  }, [activeTable]);

  // Determine Primary Key column dynamically
  const getPrimaryKeyColumn = (tableName) => {
    if (tableName === 'otps') return 'email';
    if (tableName === 'room_interviewers' || tableName === 'interview_report_email_logs' || tableName === 'interview_transcripts') return 'room_id';
    return 'id'; // Default for users, interview_rooms, candidate_profiles, etc.
  };

  const handleEditClick = (row) => {
    setEditingRow(row);
    setEditFormData({ ...row });
    setUnlockedFields({});
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    setEditError('');

    const pkColumn = getPrimaryKeyColumn(activeTable);
    const pkValue = editingRow[pkColumn];

    try {
      const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
      const headers = { 'x-user-id': currentUser.id };

      const payload = {
        pkColumn,
        pkValue,
        data: editFormData
      };

      await api.put(`/api/superadmin/tenants/${tenant.id}/tables/${activeTable}/rows`, payload, { headers });

      // Refresh and close
      setEditingRow(null);
      fetchTableData(activeTable);
      addToast('Row updated successfully!');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update row data.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleInputChange = (colName, val) => {
    setEditFormData(prev => ({
      ...prev,
      [colName]: val
    }));
  };

  const handleHardDeleteSubmit = async () => {
    setDeleteSubmitting(true);
    setDeleteError('');
    const pkColumn = getPrimaryKeyColumn(activeTable);
    const pkValue = deleteRowItem[pkColumn];

    try {
      const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
      const headers = { 'x-user-id': currentUser.id };
      await api.delete(`/api/superadmin/tenants/${tenant.id}/tables/${activeTable}/rows`, { 
        headers, 
        data: { pkColumn, pkValue } 
      });
      setDeleteRowItem(null);
      fetchTableData(activeTable);
      addToast('Row permanently deleted!');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete row.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSoftDeleteSubmit = async () => {
    setDeleteSubmitting(true);
    setDeleteError('');
    const pkColumn = getPrimaryKeyColumn(activeTable);
    const pkValue = softDeleteRowItem[pkColumn];

    try {
      const currentUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
      const headers = { 'x-user-id': currentUser.id };
      await api.patch(`/api/superadmin/tenants/${tenant.id}/tables/${activeTable}/rows/soft-delete`, { pkColumn, pkValue }, { headers });
      setSoftDeleteRowItem(null);
      fetchTableData(activeTable);
      addToast('Row suspended successfully!');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to soft delete row.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Filter rows
  const filteredRows = tableData.rows.filter(row => {
    if (!searchQuery.trim()) return true;
    return Object.values(row).some(val =>
      String(val || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div style={styles.detailLayoutContainer}>
      {/* Header */}
      <div style={styles.detailHeader}>
        <button onClick={onBack} style={styles.backButton}>
          ← Back to Console
        </button>
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={styles.detailTitle}>{tenant.org_name}</h2>
            <span style={styles.subdomainBadge}>{tenant.subdomain}</span>
          </div>
          <p style={styles.detailSubtitle}>
            Created on {new Date(tenant.created_at).toLocaleDateString()} by <strong>{tenant.created_by || 'Self Signup'}</strong>
          </p>
        </div>
      </div>

      <div style={styles.inspectorSplit}>
        {/* Left Sidebar Table Selector */}
        <div style={styles.inspectorSidebar}>
          <h4 style={styles.inspectorSidebarTitle}>Database Tables</h4>
          <div style={styles.inspectorList}>
            {TABLES.map((t) => (
              <button
                key={t.key}
                onClick={() => { setActiveTable(t.key); setSearchQuery(''); }}
                style={{
                  ...styles.inspectorBtn,
                  ...(activeTable === t.key ? styles.inspectorBtnActive : {})
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right Data Grid Panel */}
        <div style={styles.inspectorMainPanel}>
          <div style={styles.detailToolbar}>
            <h3 style={styles.tableTitleText}>
              Viewing Table: <code>{activeTable}</code> ({tableData.rows.length} rows)
            </h3>
            <div style={styles.actions}>
              <input
                type="text"
                placeholder="Filter table rows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          {tableError && (
            <div style={styles.tableErrorBox}>
              ⚠️ {tableError}
            </div>
          )}

          {loadingTable ? (
            <TableSkeleton />
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {tableData.columns.map(col => (
                      <th key={col} style={styles.th}>{col}</th>
                    ))}
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <EmptyStateTableRow colSpan={tableData.columns.length + 1} message={`No records found inside ${activeTable}.`} />
                  ) : filteredRows.map((row, idx) => {
                    const pkCol = getPrimaryKeyColumn(activeTable);
                    const rowKey = row[pkCol] !== undefined ? `${row[pkCol]}-${idx}` : idx;
                    const isSoftDeleted = row.deleted_at !== null && row.deleted_at !== undefined;
                    return (
                      <tr key={rowKey} className="interactive-row" style={{ ...styles.tr, opacity: isSoftDeleted ? 0.6 : 1, textDecoration: isSoftDeleted ? 'line-through' : 'none' }}>
                        {tableData.columns.map(col => {
                          const val = row[col];
                          // Truncate large texts/blobs to keep table neat
                          let displayVal = val;
                          let isImage = false;
                          
                          if (val !== null && val !== undefined) {
                            if (col === 'image_data' && val.type === 'Buffer' && Array.isArray(val.data)) {
                                const base64 = arrayBufferToBase64(val.data);
                                const mime = row['image_mime'] || 'image/png';
                                displayVal = (
                                  <img 
                                    src={`data:${mime};base64,${base64}`} 
                                    alt="screenshot" 
                                    style={{ maxWidth: '120px', maxHeight: '80px', borderRadius: '4px', border: '1px solid var(--border-color)', objectFit: 'contain' }} 
                                  />
                                );
                                isImage = true;
                            } else if (typeof val === 'object') {
                              displayVal = JSON.stringify(val);
                            } else if (typeof val === 'string' && val.length > 50) {
                              displayVal = val.slice(0, 50) + '...';
                            }
                          }
                          return (
                            <td
                              key={col}
                              style={styles.td}
                              title={!isImage && val !== null && val !== undefined ? String(val) : ''}
                            >
                              {displayVal === null || displayVal === undefined ? (
                                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>NULL</span>
                              ) : isImage ? (
                                displayVal
                              ) : String(displayVal)}
                            </td>
                          );
                        })}
                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleEditClick(row)}
                            style={{ ...styles.actionBtnBase, ...styles.editBtn }}
                          >
                            Edit
                          </button>
                          {!isSoftDeleted && (
                            <button
                              onClick={() => setSoftDeleteRowItem(row)}
                              style={{ ...styles.actionBtnBase, ...styles.disableBtn }}
                            >
                              Disable
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteRowItem(row)}
                            style={{ ...styles.actionBtnBase, ...styles.deleteBtn }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC EDIT MODAL */}
      {editingRow && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto'}}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, margin: 0}}>Modify Table Record</h3>
              <button 
                type="button" 
                onClick={() => setEditingRow(null)} 
                style={styles.modalCloseX}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            <p style={{fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px', marginTop: '-8px'}}>
              Table: <code>{activeTable}</code> | Primary Key: <code>{getPrimaryKeyColumn(activeTable)} = {editingRow[getPrimaryKeyColumn(activeTable)]}</code>
            </p>

            <form onSubmit={handleEditSubmit} style={styles.form}>
              {tableData.columns.map((col) => {
                const isPk = col === getPrimaryKeyColumn(activeTable);
                const isReadonlyMeta = col === 'created_at' || col === 'updated_at' || col === 'completed_at' || col === 'cancelled_at';
                const isFieldDisabled = isPk || isReadonlyMeta;
                const isEditable = !isFieldDisabled;

                return (
                  <div key={col} style={styles.formGroup}>
                    <label style={{...styles.label, fontWeight: '700'}}>
                      {col} {isFieldDisabled && <span style={{fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal'}}>(Read-Only)</span>}
                    </label>

                    <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                      {unlockedFields[col] || isFieldDisabled ? (
                        // Edit/Readonly Input
                        typeof editingRow[col] === 'string' && editingRow[col].length > 100 ? (
                          <textarea
                            disabled={isFieldDisabled}
                            value={editFormData[col] === null || editFormData[col] === undefined ? '' : editFormData[col]}
                            onChange={(e) => handleInputChange(col, e.target.value)}
                            style={{...styles.formInput, width: '100%', paddingRight: '36px', minHeight: '60px', fontFamily: 'inherit', resize: 'vertical'}}
                          />
                        ) : (
                          <input
                            type="text"
                            disabled={isFieldDisabled}
                            value={editFormData[col] === null || editFormData[col] === undefined ? '' : editFormData[col]}
                            onChange={(e) => handleInputChange(col, e.target.value)}
                            style={{...styles.formInput, width: '100%', paddingRight: '36px'}}
                          />
                        )
                      ) : (
                        // Locked Display Input (looks read-only)
                        <input
                          type="text"
                          disabled
                          value={editFormData[col] === null || editFormData[col] === undefined ? 'NULL' : editFormData[col]}
                          style={{
                            ...styles.formInput,
                            width: '100%',
                            paddingRight: '36px',
                            background: 'var(--bg-surface-alt)',
                            borderColor: 'transparent',
                            cursor: 'default',
                            color: 'var(--text-primary)',
                            opacity: 0.95,
                          }}
                        />
                      )}

                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => {
                            setUnlockedFields(prev => ({
                              ...prev,
                              [col]: !prev[col]
                            }));
                          }}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: typeof editingRow[col] === 'string' && editingRow[col].length > 100 ? '10px' : '50%',
                            transform: typeof editingRow[col] === 'string' && editingRow[col].length > 100 ? 'none' : 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px',
                            borderRadius: '4px',
                            color: unlockedFields[col] ? '#10b981' : 'var(--text-muted)',
                            zIndex: 2,
                            transition: 'color 0.15s ease'
                          }}
                          title={unlockedFields[col] ? "Lock & save field value" : "Unlock field to edit"}
                        >
                          {unlockedFields[col] ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4z" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {editError && <p style={styles.modalError}>{editError}</p>}

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setEditingRow(null)} style={styles.cancelButton} disabled={editSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.confirmButton} disabled={editSubmitting}>
                  {editSubmitting ? 'Saving changes...' : 'Save Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HARD DELETE MODAL */}
      {deleteRowItem && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: '400px'}}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, margin: 0, color: '#ef4444'}}>Confirm Hard Delete</h3>
            </div>
            <p style={{marginBottom: '20px', fontSize: '14px'}}>
              Are you sure you want to permanently delete this record? This action cannot be undone.
            </p>
            {deleteError && <p style={styles.modalError}>{deleteError}</p>}
            <div style={styles.modalActions}>
              <button type="button" onClick={() => setDeleteRowItem(null)} style={styles.cancelButton} disabled={deleteSubmitting}>
                Cancel
              </button>
              <button type="button" onClick={handleHardDeleteSubmit} style={{...styles.confirmButton, background: '#ef4444'}} disabled={deleteSubmitting}>
                {deleteSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISABLE MODAL */}
      {softDeleteRowItem && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modal, maxWidth: '400px'}}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, margin: 0, color: '#f59e0b'}}>Confirm Disable</h3>
            </div>
            <p style={{marginBottom: '20px', fontSize: '14px'}}>
              Are you sure you want to disable this record? It will be marked as deleted/disabled but the data will remain in the database.
            </p>
            {deleteError && <p style={styles.modalError}>{deleteError}</p>}
            <div style={styles.modalActions}>
              <button type="button" onClick={() => setSoftDeleteRowItem(null)} style={styles.cancelButton} disabled={deleteSubmitting}>
                Cancel
              </button>
              <button type="button" onClick={handleSoftDeleteSubmit} style={{...styles.confirmButton, background: '#f59e0b', color: '#fff'}} disabled={deleteSubmitting}>
                {deleteSubmitting ? 'Disabling...' : 'Disable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  pageLayout: {
    minHeight: 'calc(100vh - 78px)',
    width: '100%',
    background: 'var(--bg-page)',
    color: 'var(--text-secondary)',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    display: 'flex',
    boxSizing: 'border-box',
  },
  sidebar: {
    width: '200px',
    background: 'var(--bg-surface)',
    borderRight: '1px solid var(--border-color)',
    padding: '14px 0 10px 0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  contentArea: {
    flex: 1,
    padding: '24px 20px',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  sidebarMenuText: {
    fontSize: '10.5px',
    fontWeight: '700',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    padding: '0 14px',
    marginBottom: '6px',
  },
  sidebarItem: {
    padding: '7px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    textAlign: 'left',
    color: 'var(--text-secondary)',
    fontSize: '12.5px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
    outline: 'none',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sidebarItemActive: {
    background: 'var(--primary-color)',
    color: '#ffffff',
    boxShadow: '0 3px 10px rgba(79, 70, 229, 0.25)',
  },

  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '24px',
  },

  title: {
    fontSize: '32px',
    fontWeight: '800',
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '-1px',
  },

  subtitle: {
    color: 'var(--text-muted)',
    fontSize: '15px',
    marginTop: '6px',
    margin: 0,
  },

  logoutButton: {
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#f87171',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },

  changePasswordButton: {
    background: 'none',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    padding: '10px 18px',
    color: 'var(--text-secondary)',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '24px',
    marginBottom: '40px',
  },

  statCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
  },

  statLabel: {
    color: 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
  },

  statValue: {
    fontSize: '36px',
    fontWeight: '800',
    color: 'var(--text-primary)',
  },

  workspace: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-card)',
  },

  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    background: 'var(--bg-surface-alt)',
    borderBottom: '1px solid var(--border-color)',
    flexWrap: 'wrap',
    gap: '16px',
  },

  tabs: {
    display: 'flex',
    gap: '8px',
  },

  tab: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-muted)',
    padding: '8px 16px',
    fontWeight: '600',
    fontSize: '14px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'color 0.2s, background 0.2s',
  },

  activeTab: {
    background: 'var(--bg-card)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-color)',
  },

  actions: {
    display: 'flex',
    gap: '12px',
  },

  searchInput: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    padding: '8px 16px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    width: '240px',
    outline: 'none',
  },

  createButton: {
    background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 18px',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(29, 78, 216, 0.2)',
  },

  tableWrapper: {
    overflowX: 'auto',
    maxHeight: 'calc(100vh - 250px)',
    overflowY: 'auto',
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
  },

  modalCloseX: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
    transition: 'color 0.15s ease',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },

  th: {
    background: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    fontWeight: '600',
    fontSize: '13px',
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-color)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },

  tr: {
    borderBottom: '1px solid var(--border-color)',
    transition: 'background 0.2s',
  },

  td: {
    padding: '18px 24px',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },

  subdomainBadge: {
    background: 'var(--bg-surface-alt)',
    color: 'var(--text-secondary)',
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
  },

  editLink: {
    color: '#3b82f6',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    marginRight: '16px',
    padding: 0,
  },

  deleteLink: {
    color: '#ef4444',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    padding: 0,
  },

  emptyRow: {
    padding: '40px 24px',
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: '14px',
  },

  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
    color: 'var(--text-muted)',
  },

  spinner: {
    border: '3px solid var(--border-color)',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'var(--bg-overlay)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modal: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    padding: '32px',
    boxSizing: 'border-box',
    boxShadow: 'var(--shadow-lg)',
  },

  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginTop: 0,
    marginBottom: '24px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    letterSpacing: '0.2px',
  },

  formInput: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    padding: '10px 14px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
  },

  inputHelp: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    lineHeight: '1.4',
  },

  formDivider: {
    fontSize: '12px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginTop: '12px',
    marginBottom: '4px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '6px',
  },

  modalError: {
    color: '#f87171',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    padding: '10px',
    borderRadius: '6px',
    fontSize: '13px',
    margin: 0,
  },

  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },

  cancelButton: {
    background: 'transparent',
    border: '1px solid var(--border-input)',
    color: 'var(--text-secondary)',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
  },

  confirmButton: {
    background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
  },

  dangerButton: {
    background: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
  },

  modalWarnText: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: '1.6',
    margin: '0 0 16px 0',
  },

  detailLayoutContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '18px',
  },
  backButton: {
    alignSelf: 'flex-start',
    background: 'none',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    padding: '8px 14px',
    color: 'var(--text-secondary)',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
  },
  detailTitle: {
    fontSize: '24px',
    fontWeight: '800',
    color: 'var(--text-primary)',
    margin: 0,
  },
  detailSubtitle: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    margin: '6px 0 0 0',
  },
  detailStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  detailStatCard: {
    background: 'var(--bg-surface-alt)',
    borderLeft: '4px solid var(--accent-color, #4f46e5)',
    borderTop: '1px solid var(--border-color)',
    borderRight: '1px solid var(--border-color)',
    borderBottom: '1px solid var(--border-color)',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailStatLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailStatValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: 'var(--text-primary)',
  },
  detailStatSub: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  detailWorkspace: {
    background: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border-color)',
    padding: '20px',
    boxShadow: 'var(--shadow-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '14px',
  },
  detailSelect: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    padding: '8px 12px',
    color: 'var(--text-primary)',
    fontSize: '13px',
    outline: 'none',
    cursor: 'pointer',
  },
  tenantLinkBtn: {
    background: 'none',
    border: 'none',
    padding: '0',
    margin: '0',
    color: 'var(--accent-color, #4f46e5)',
    fontWeight: '700',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    textDecoration: 'none',
    outline: 'none',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-block',
    letterSpacing: '0.5px'
  },
  roleBadge: {
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    display: 'inline-block'
  },
  inspectorSplit: {
    display: 'flex',
    gap: '24px',
    marginTop: '10px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  inspectorSidebar: {
    width: '260px',
    background: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border-color)',
    padding: '20px',
    boxShadow: 'var(--shadow-sm)',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  inspectorSidebarTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: '0 0 16px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  inspectorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inspectorBtn: {
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 14px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    outline: 'none',
    fontFamily: 'inherit',
  },
  inspectorBtnActive: {
    background: 'var(--accent-color, #4f46e5)',
    color: '#fff',
  },
  inspectorMainPanel: {
    flex: 1,
    minWidth: '320px',
    background: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border-color)',
    padding: '20px',
    boxShadow: 'var(--shadow-sm)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflowX: 'auto',
  },
  tableTitleText: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0,
  },
  tableErrorBox: {
    color: '#ef4444',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.15)',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
  },
  editRowLink: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-color, #4f46e5)',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  actionBtnBase: {
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid transparent',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginRight: '8px',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    outline: 'none',
  },
  editBtn: {
    color: 'var(--accent-color, #4f46e5)',
    background: 'rgba(79, 70, 229, 0.1)',
    borderColor: 'rgba(79, 70, 229, 0.2)',
  },
  disableBtn: {
    color: '#f59e0b',
    background: 'rgba(245, 158, 11, 0.1)',
    borderColor: 'rgba(245, 158, 11, 0.2)',
  },
  deleteBtn: {
    color: '#ef4444',
    background: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.2)',
  }
};

export default SuperadminDashboard;
